import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';
import ImportModal from '../../components/Common/ImportModal';
import { ocorrenciasImportConfig } from '../../config/importConfigs';

// Interface alinhada com a estrutura real do banco de dados PostgreSQL
interface Occurrence {
  id: number;
  titulo: string;
  descricao: string;
  tipo: string;
  gravidade: string;
  status: string;
  data_ocorrencia: string;
  data_resolucao: string | null;
  veiculo_id: number | null;
  motorista_id: number | null;
  usuario_registro_id: number | null;
  local: string | null;
  responsavel: string | null;
  observacoes_resolucao: string | null;
  fotos: any | null;
  empresa_id: number;
  criado_em: string;
  atualizado_em: string;
  // Campos adicionais do frontend (vindos de joins)
  veiculo_placa?: string;
  motorista_nome?: string;
}

const Occurrences: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Hook de infinite scroll
  const {
    items: occurrences,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<Occurrence>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getOccurrences({
          page,
          limit,
          status: filters?.status || undefined,
          tipo: filters?.tipo || undefined,
          gravidade: filters?.gravidade || undefined
        });

        if (response.success && response.data) {
          const ocorrenciasApi = response.data.ocorrencias || [];
          const ocorrenciasMapeadas: Occurrence[] = ocorrenciasApi.map((ocorrencia: any) => ({
            id: ocorrencia.id,
            titulo: ocorrencia.titulo,
            descricao: ocorrencia.descricao,
            tipo: ocorrencia.tipo,
            gravidade: ocorrencia.gravidade,
            status: ocorrencia.status,
            data_ocorrencia: ocorrencia.data_ocorrencia,
            data_resolucao: ocorrencia.data_resolucao,
            veiculo_id: ocorrencia.veiculo_id,
            motorista_id: ocorrencia.motorista_id,
            usuario_registro_id: ocorrencia.usuario_registro_id,
            local: ocorrencia.local,
            responsavel: ocorrencia.responsavel,
            observacoes_resolucao: ocorrencia.observacoes_resolucao,
            fotos: ocorrencia.fotos,
            empresa_id: ocorrencia.empresa_id,
            criado_em: ocorrencia.criado_em,
            atualizado_em: ocorrencia.atualizado_em,
            veiculo_placa: ocorrencia.veiculo_placa,
            motorista_nome: ocorrencia.motorista_nome
          }));

          return {
            success: true,
            data: {
              items: ocorrenciasMapeadas,
              pagination: {
                total: response.data.pagination?.total || ocorrenciasMapeadas.length
              }
            }
          };
        }

        return {
          success: true,
          data: { items: [], pagination: { total: 0 } }
        };
      } catch (error) {
        console.error('Erro ao carregar ocorrências:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar ocorrências'
        };
      }
    },
    itemsPerPage: 10,
    filters: { status: statusFilter, tipo: typeFilter, gravidade: severityFilter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const formatStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pendente': 'Pendente',
      'em_andamento': 'Em Andamento',
      'resolvida': 'Resolvida'
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status: string): string => {
    // Normaliza o status para lowercase para comparação
    const statusLower = status.toLowerCase();

    const statusMap: Record<string, string> = {
      'pendente': 'status-danger',
      'em_andamento': 'status-warning',
      'em andamento': 'status-warning',
      'resolvida': 'status-success'
    };
    return statusMap[statusLower] || statusMap[status] || 'status-pending';
  };

  const formatGravidade = (gravidade: string): string => {
    const gravidadeMap: Record<string, string> = {
      'baixa': 'Baixa',
      'media': 'Média',
      'alta': 'Alta'
    };
    return gravidadeMap[gravidade] || gravidade;
  };

  const getSeverityClass = (gravidade: string): string => {
    // Normaliza a gravidade para lowercase para comparação
    const gravidadeLower = gravidade.toLowerCase();

    const severityMap: Record<string, string> = {
      'baixa': 'status-info',
      'media': 'status-warning',
      'média': 'status-warning',
      'alta': 'status-danger'
    };
    return severityMap[gravidadeLower] || severityMap[gravidade] || 'status-secondary';
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTimeSince = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    return `${diffHours}h`;
  };

  const filteredOccurrences = occurrences.filter(occurrence => {
    const matchesSearch = occurrence.titulo.toLowerCase().includes(filter.toLowerCase()) ||
                         occurrence.descricao.toLowerCase().includes(filter.toLowerCase()) ||
                         (occurrence.veiculo_placa && occurrence.veiculo_placa.toLowerCase().includes(filter.toLowerCase())) ||
                         (occurrence.motorista_nome && occurrence.motorista_nome.toLowerCase().includes(filter.toLowerCase()));
    const matchesType = !typeFilter || occurrence.tipo === typeFilter;
    const matchesStatus = !statusFilter || occurrence.status === statusFilter;
    const matchesSeverity = !severityFilter || occurrence.gravidade === severityFilter;
    return matchesSearch && matchesType && matchesStatus && matchesSeverity;
  });

  const viewOccurrence = (id: number) => {
    navigate(`/ocorrencias/visualizar/${id}`);
  };

  const editOccurrence = (id: number) => {
    navigate(`/ocorrencias/editar/${id}`);
  };

  const deleteOccurrence = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir esta ocorrência?')) {
      try {
        // Usar API real para excluir ocorrência
        const response = await apiService.deleteOccurrence(id);

        if (response.success) {
          utils.showNotification('Ocorrência excluída com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          utils.showNotification('Erro ao excluir ocorrência', { type: 'error' });
        }
      } catch (error) {
        console.error('Erro ao excluir ocorrência:', error);
        utils.showNotification('Erro ao excluir ocorrência', { type: 'error' });
      }
    }
  };

  const getTypes = () => {
    const types = Array.from(new Set(occurrences.map(o => o.tipo)));
    return types;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(occurrences.map(o => o.status)));
    return statuses;
  };

  const getSeverities = () => {
    const severities = Array.from(new Set(occurrences.map(o => o.gravidade)));
    return severities;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Ocorrências</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Nova Ocorrência
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando ocorrências...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Ocorrências</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.OCORRENCIAS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.OCORRENCIAS) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/ocorrencias/nova')}
            >
              <i className="fas fa-plus"></i>
              Nova Ocorrência
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Registro de Ocorrências</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Título, descrição, veículo ou motorista...'
                },
                {
                  type: 'select',
                  label: 'Tipo',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getTypes().map(type => ({ value: type, label: type }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getStatuses().map(status => ({ value: status, label: status }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Gravidade',
                  value: severityFilter,
                  onChange: setSeverityFilter,
                  options: [
                    { value: '', label: 'Todas' },
                    ...getSeverities().map(severity => ({ value: severity, label: severity }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setTypeFilter('');
                setStatusFilter('');
                setSeverityFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Ocorrências"
            />
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredOccurrences.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="ocorrências"
              />
            )}

            {filteredOccurrences.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-exclamation-triangle empty-state-icon"></i>
                <h3>Nenhuma ocorrência encontrada</h3>
                <p>{filter || typeFilter || statusFilter || severityFilter ? 'Tente ajustar os filtros de busca.' : 'Registre a primeira ocorrência do sistema.'}</p>
                {canCreate(SystemModule.OCORRENCIAS) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/ocorrencias/nova')}
                  >
                    Registrar Primeira Ocorrência
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Título</th>
                      <th>Tipo</th>
                      <th>Gravidade</th>
                      <th>Veículo</th>
                      <th>Motorista</th>
                      <th>Status</th>
                      <th>Tempo</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOccurrences.map(occurrence => (
                      <tr key={occurrence.id}>
                        <td>
                          <div style={{ fontSize: '0.8rem' }}>
                            {formatDate(occurrence.data_ocorrencia)}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#7f8c8d' }}>
                            {formatDateTime(occurrence.data_ocorrencia).split(', ')[1]}
                          </div>
                        </td>
                        <td>
                          <strong>{occurrence.titulo}</strong>
                          {occurrence.descricao && (
                            <div style={{ fontSize: '0.8rem', color: '#7f8c8d', maxWidth: '200px' }}>
                              {occurrence.descricao.length > 60 
                                ? `${occurrence.descricao.substring(0, 60)}...` 
                                : occurrence.descricao}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="status-badge status-info">
                            {occurrence.tipo}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getSeverityClass(occurrence.gravidade)}`}>
                            {formatGravidade(occurrence.gravidade)}
                          </span>
                        </td>
                        <td>{occurrence.veiculo_placa || '-'}</td>
                        <td>{occurrence.motorista_nome || '-'}</td>
                        <td>
                          <span className={`status-badge ${getStatusClass(occurrence.status)}`}>
                            {formatStatus(occurrence.status)}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                            {getTimeSince(occurrence.data_ocorrencia)}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => viewOccurrence(occurrence.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {canUpdate(SystemModule.OCORRENCIAS) && (
                              <button
                                className="btn-edit"
                                onClick={() => editOccurrence(occurrence.id)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.OCORRENCIAS) && (
                              <button
                                className="btn-delete"
                                onClick={() => deleteOccurrence(occurrence.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas das ocorrências */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-value">
              {occurrences.length}
            </div>
            <div className="stat-label">Total de Ocorrências</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {occurrences.filter(o => o.status?.toLowerCase() === 'resolvida').length}
            </div>
            <div className="stat-label">Resolvidas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-value">
              {occurrences.filter(o => (o.status || '').toLowerCase().replace('_', ' ') === 'em andamento').length}
            </div>
            <div className="stat-label">Em Andamento</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="stat-value">
              {occurrences.filter(o => o.status?.toLowerCase() === 'pendente').length}
            </div>
            <div className="stat-label">Pendentes</div>
          </div>
        </div>

        {/* Distribuição por gravidade */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Gravidade</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getSeverities().map(severity => {
                const severityOccurrences = occurrences.filter(o => o.gravidade === severity);
                const percentage = Math.round((severityOccurrences.length / occurrences.length) * 100);
                
                return (
                  <div key={severity} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{severity}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{severityOccurrences.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: 'linear-gradient(135deg, #04C64F, #03a542)', 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Resolvidas: <strong>{severityOccurrences.filter(o => o.status?.toLowerCase() === 'resolvida').length}</strong></div>
                      <div>Pendentes: <strong>{severityOccurrences.filter(o => o.status?.toLowerCase() === 'pendente').length}</strong></div>
                      <div>Em andamento: <strong>{severityOccurrences.filter(o => (o.status || '').toLowerCase().replace('_', ' ') === 'em andamento').length}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Alertas de ocorrências críticas */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Ocorrências Críticas</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div>
                <h4 style={{ color: '#e74c3c', marginBottom: '10px' }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                  Ocorrências de Alta Gravidade Pendentes
                </h4>
                <ul style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {occurrences
                    .filter(o => o.gravidade?.toLowerCase() === 'alta' && o.status?.toLowerCase() === 'pendente')
                    .map(occurrence => (
                      <li key={occurrence.id}>
                        <strong>{occurrence.titulo}</strong> - {occurrence.veiculo_placa || '-'} ({getTimeSince(occurrence.data_ocorrencia)})
                      </li>
                    ))
                  }
                  {occurrences.filter(o => o.gravidade?.toLowerCase() === 'alta' && o.status?.toLowerCase() === 'pendente').length === 0 && (
                    <li>Nenhuma ocorrência crítica pendente</li>
                  )}
                </ul>
              </div>

              <div>
                <h4 style={{ color: '#f39c12', marginBottom: '10px' }}>
                  <i className="fas fa-clock" style={{ marginRight: '8px' }}></i>
                  Ocorrências em Andamento
                </h4>
                <ul style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {occurrences
                    .filter(o => (o.status || '').toLowerCase().replace('_', ' ') === 'em andamento')
                    .map(occurrence => (
                      <li key={occurrence.id}>
                        <strong>{occurrence.titulo}</strong> - {occurrence.veiculo_placa || '-'} ({getTimeSince(occurrence.data_ocorrencia)})
                      </li>
                    ))
                  }
                  {occurrences.filter(o => (o.status || '').toLowerCase().replace('_', ' ') === 'em andamento').length === 0 && (
                    <li>Nenhuma ocorrência em andamento</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={ocorrenciasImportConfig}
      />
    </div>
  );
};

export default Occurrences;
